const POLICY_MODES = new Set(['off', 'essential', 'demo_limited', 'demo_full']);
const OPAQUE_DEMO_ID = /^dms_[a-z0-9_-]{20,160}$/i;
const OPAQUE_CLAIM_ID = /^clm_[a-z0-9_-]{20,160}$/i;
const OPAQUE_POLICY_TOKEN = /^ptk_[a-z0-9_-]{20,160}$/i;
const OPAQUE_SHOP_ID = /^(?:public_demo_prestashop|shp_[a-f0-9]{32})$/i;
const AUTHORITY_STORAGE_KEY = 'kea_demo_telemetry_authority_v1';
const WITHDRAW_TIMEOUT_MS = 2000;
let authorityGeneration = 0;
let latestLoadRequest = 0;

export const ESSENTIAL_TELEMETRY_POLICY = Object.freeze({
  mode: 'essential',
  shopPublicId: null,
  demoSessionId: null,
  policyVersion: 0,
  expiresAt: null,
});

export const OFF_TELEMETRY_POLICY = Object.freeze({
  mode: 'off',
  shopPublicId: null,
  demoSessionId: null,
  policyVersion: 0,
  expiresAt: null,
});

function isNonEmptyString(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

function readStoredAuthority(session) {
  if (typeof window === 'undefined' || !window.sessionStorage || !session?.gatewayToken) return null;
  try {
    const value = JSON.parse(window.sessionStorage.getItem(AUTHORITY_STORAGE_KEY) || 'null');
    if (!value
      || value.gatewayToken !== session.gatewayToken
      || !OPAQUE_DEMO_ID.test(String(value.demoSessionId || ''))
      || !OPAQUE_POLICY_TOKEN.test(String(value.policyToken || ''))
    ) {
      return null;
    }
    return { demoSessionId: value.demoSessionId, policyToken: value.policyToken };
  } catch (_error) {
    return null;
  }
}

function storeAuthority(session, demoSessionId, policyToken) {
  if (typeof window === 'undefined' || !window.sessionStorage || !session?.gatewayToken) return;
  if (!OPAQUE_DEMO_ID.test(String(demoSessionId || '')) || !OPAQUE_POLICY_TOKEN.test(String(policyToken || ''))) return;
  window.sessionStorage.setItem(AUTHORITY_STORAGE_KEY, JSON.stringify({
    gatewayToken: session.gatewayToken,
    demoSessionId,
    policyToken,
  }));
}

export function clearStoredTelemetryAuthority() {
  authorityGeneration += 1;
  latestLoadRequest += 1;
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  window.sessionStorage.removeItem(AUTHORITY_STORAGE_KEY);
}

export function normalizeTelemetryHandoff(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const demoSessionId = String(value.demoSessionId || '');
  const claimId = String(value.claimId || '');
  if (!OPAQUE_DEMO_ID.test(demoSessionId) || !OPAQUE_CLAIM_ID.test(claimId)) return null;
  return { demoSessionId, claimId };
}

export function normalizeTelemetryPolicy(value, nowMs = Date.now()) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return ESSENTIAL_TELEMETRY_POLICY;
  }

  const mode = String(value.mode || '');
  const policyVersion = Number(value.policyVersion);

  if (mode === 'off') {
    const hasAuthority = value.shopPublicId != null
      || value.demoSessionId != null
      || value.expiresAt != null
      || value.policyToken != null;
    return policyVersion === 0 && !hasAuthority
      ? OFF_TELEMETRY_POLICY
      : ESSENTIAL_TELEMETRY_POLICY;
  }

  const shopPublicId = String(value.shopPublicId || '');
  const expiresAtMs = Date.parse(String(value.expiresAt || ''));
  if (!POLICY_MODES.has(mode)
    || !Number.isInteger(policyVersion)
    || policyVersion < 1
    || !OPAQUE_SHOP_ID.test(shopPublicId)
    || !Number.isFinite(expiresAtMs)
    || expiresAtMs <= nowMs
  ) {
    return ESSENTIAL_TELEMETRY_POLICY;
  }

  if (mode === 'demo_full' || mode === 'demo_limited') {
    const demoSessionId = String(value.demoSessionId || '');
    if (shopPublicId !== 'public_demo_prestashop' || !OPAQUE_DEMO_ID.test(demoSessionId)) {
      return ESSENTIAL_TELEMETRY_POLICY;
    }
    return {
      mode,
      shopPublicId,
      demoSessionId,
      policyVersion,
      expiresAt: new Date(expiresAtMs).toISOString(),
    };
  }

  return {
    mode,
    shopPublicId,
    demoSessionId: null,
    policyVersion,
    expiresAt: new Date(expiresAtMs).toISOString(),
  };
}

async function readTelemetryHandoff(bridge) {
  if (!bridge || typeof bridge.sendMessageWithResponse !== 'function') return null;
  try {
    const response = await bridge.sendMessageWithResponse('app.bootstrap', {});
    if (!response || response.result === 'error') return null;
    return normalizeTelemetryHandoff(response.telemetryHandoff);
  } catch (_error) {
    return null;
  }
}

export async function loadAuthoritativeTelemetryPolicy({
  bridge,
  session,
  fetchImpl,
  signal,
}) {
  if (!session
    || !isNonEmptyString(session.gatewayBaseUrl)
    || !isNonEmptyString(session.gatewayToken)
    || !isNonEmptyString(session.gatewayAccessToken)
  ) {
    return ESSENTIAL_TELEMETRY_POLICY;
  }

  const doFetch = fetchImpl || (typeof fetch === 'function' ? fetch : null);
  if (!doFetch) return ESSENTIAL_TELEMETRY_POLICY;

  const requestGeneration = authorityGeneration;
  const requestId = ++latestLoadRequest;
  const isCurrentRequest = () => requestGeneration === authorityGeneration
    && requestId === latestLoadRequest
    && !(signal && signal.aborted);

  const storedAuthority = readStoredAuthority(session);
  const handoff = storedAuthority || await readTelemetryHandoff(bridge);
  if (!isCurrentRequest()) return ESSENTIAL_TELEMETRY_POLICY;

  // Older plugin/gateway deployments do not expose the telemetry-policy
  // endpoints.  Keep those installations on the fail-closed essential policy
  // without probing a route that is not part of their advertised contract.
  // A demo handoff (or previously stored authority) is itself sufficient proof
  // that the endpoint exists; ordinary shops must advertise the capability.
  if (!handoff && session.capabilities?.telemetryPolicyV1 !== true) {
    return ESSENTIAL_TELEMETRY_POLICY;
  }

  const baseUrl = session.gatewayBaseUrl.replace(/\/+$/, '');
  const url = `${baseUrl}/v1/installations/${encodeURIComponent(session.gatewayToken)}/telemetry/bootstrap`;

  try {
    const response = await doFetch(url, {
      method: 'POST',
      credentials: 'omit',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${session.gatewayAccessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ handoff }),
      signal,
    });
    if (!response || !response.ok || !isCurrentRequest()) return ESSENTIAL_TELEMETRY_POLICY;
    const payload = await response.json();
    if (!isCurrentRequest()) return ESSENTIAL_TELEMETRY_POLICY;
    const policy = normalizeTelemetryPolicy(payload && payload.telemetry);
    const policyToken = payload?.telemetry?.policyToken;
    if ((policy.mode === 'demo_full' || policy.mode === 'demo_limited')
      && OPAQUE_POLICY_TOKEN.test(String(policyToken || ''))
    ) {
      storeAuthority(session, policy.demoSessionId, policyToken);
    } else if (storedAuthority) {
      clearStoredTelemetryAuthority();
    }
    return policy;
  } catch (_error) {
    return ESSENTIAL_TELEMETRY_POLICY;
  }
}

export async function withdrawAuthoritativeTelemetryPolicy({
  session,
  fetchImpl,
  timeoutMs = WITHDRAW_TIMEOUT_MS,
}) {
  const authority = readStoredAuthority(session);
  clearStoredTelemetryAuthority();
  if (!authority
    || !session
    || !isNonEmptyString(session.gatewayBaseUrl)
    || !isNonEmptyString(session.gatewayToken)
    || !isNonEmptyString(session.gatewayAccessToken)
  ) {
    return false;
  }

  const doFetch = fetchImpl || (typeof fetch === 'function' ? fetch : null);
  if (!doFetch) return false;
  const baseUrl = session.gatewayBaseUrl.replace(/\/+$/, '');
  const boundedTimeoutMs = Number.isFinite(timeoutMs) && timeoutMs > 0
    ? Math.min(timeoutMs, WITHDRAW_TIMEOUT_MS)
    : WITHDRAW_TIMEOUT_MS;
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  let timeoutId = null;
  try {
    const request = Promise.resolve()
      .then(() => doFetch(
        `${baseUrl}/v1/installations/${encodeURIComponent(session.gatewayToken)}/telemetry/withdraw`,
        {
          method: 'POST',
          credentials: 'omit',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${session.gatewayAccessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ handoff: authority }),
          signal: controller ? controller.signal : undefined,
        },
      ))
      .catch(() => null);
    const timeout = new Promise((resolveTimeout) => {
      timeoutId = setTimeout(() => {
        if (controller) controller.abort();
        resolveTimeout(null);
      }, boundedTimeoutMs);
    });
    const response = await Promise.race([request, timeout]);
    return Boolean(response && response.ok);
  } catch (_error) {
    return false;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

export const __test_only = {
  readTelemetryHandoff,
  readStoredAuthority,
  storeAuthority,
  AUTHORITY_STORAGE_KEY,
  WITHDRAW_TIMEOUT_MS,
};
